// OL
import OlMap from 'ol/Map';
import OlBaseLayer from 'ol/layer/Base';
import OlLayerGroup from 'ol/layer/Group';
import { Collection as OlCollection } from 'ol';
import { ImageWMS as OlSourceImageWMS } from 'ol/source';
import OlLayerImage from 'ol/layer/Image';

// Custom
import useApi from '@/lib/api/useApi';
import gsService from '@/services/gsService';
import { CardPosition, IGFICardOverlayData } from '@/@types/views/map';
import { DCRecord } from '@/@types/lib/dataController';
import EnumLayerFunctionality from '@/@types/services/gsServiceEnums';
import { GFIResponseType } from '@/@types/services/gsService';

const api = useApi();

const gsUrl = process.env.REACT_APP_GEOSERVERPATH || '';

type IPPHelper = {
  getPPLayers: (id: number) => Promise<OlLayerGroup | null>;
  getPPTextInfo: (data: IGFICardOverlayData) => Promise<IGFICardOverlayData | null>;
  getPPBatchOverlayData: (
    map: OlMap,
    point: number[],
    ppEntries: Array<[string, OlBaseLayer[]]>,
    position: CardPosition
  ) => Promise<IGFICardOverlayData[]>;
};

type IPPChildLayer = {
  id: number;
  code: string;
  parent_code?: string;
  naziv: string;
  gs_layer: string;
  sortorder: number;
  ttoken: string;
  gs_visible_min_zoom?: number;
  gs_visible_max_zoom?: number;
  no_features: boolean;
  gs_style_name?: string;
};

type IPPLayer = {
  id: number;
  datum_donosenja: string;
  naziv: string;
  naziv_short: string;
  tip_id: number;
  tip_naziv: string;
  tip_razina: number;
  tip_sifra: string;
  status_id: 10 | 20; // donesen, u izradi
  has_zahtjevi: boolean;
  vrste_slojeva: IPPChildLayer[];
};

export type PPTextType = {
  id: number;
  numeration: string;
  value: string;
  parent_id: number | null;
  sort: number;
  odjeljak_id?: number | null;
  sql_model_name?: 'OdClanak' | 'OdStavak' | 'OdTocka';
  children: Array<PPTextType> | null;
};

export type PPDocument = PPTextType[];

const getPPLayers = (tenantId: number): Promise<OlLayerGroup | null> =>
  api.get('prostorni-planovi').then((resp) => {
    if (!resp.data || !Array.isArray(resp.data)) {
      return null;
    }

    const plans = resp.data as IPPLayer[];

    // Split plans by status
    const activePlans = plans.filter((p: IPPLayer) => p.status_id === 10);
    const hasDraftPlans = plans.some((p: IPPLayer) => p.status_id === 20);
    const plansWithZahtjevi = plans.filter((p: IPPLayer) => p.has_zahtjevi === true);

    // ACTIVE (status_id = 10)
    const ppGroups: (OlLayerGroup | OlLayerImage<OlSourceImageWMS>)[] = activePlans.map((record: IPPLayer) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { vrste_slojeva, naziv_short, id } = record;

      // Build map of all layers
      const nodeMap = new Map<string, OlLayerGroup | OlLayerImage<OlSourceImageWMS>>();

      // First, build nodes
      (vrste_slojeva || []).forEach((layerDef) => {
        const isLeaf = !!layerDef.gs_layer;
        const layerId = layerDef.code;

        if (!layerId) return; // skip invalid

        if (isLeaf) {
          const source = new OlSourceImageWMS({
            url: gsUrl,
            params: {
              LAYERS: [layerDef.gs_layer],
              FORMAT: 'image/vnd.jpeg-png',
              tenant_id: tenantId,
              plan_id: id,
            },
          });

          const imageLayer = new OlLayerImage({ visible: true, source });
          imageLayer.set('id', `PP_${id}_${layerId}`);
          imageLayer.set('title', layerDef.naziv);
          imageLayer.set('type', 'app-defined');
          imageLayer.set('layer', layerDef.gs_layer);
          imageLayer.set('format', 'image/vnd.jpeg-png');
          imageLayer.set('sortorder', layerDef.sortorder);
          imageLayer.set('call_group', `prostorni-planovi-${id}`);
          imageLayer.set('query', true);
          imageLayer.set('params', {
            tenant_id: tenantId,
            plan_id: id,
          });

          imageLayer.set('styles', layerDef.gs_style_name);

          imageLayer.set('visible_min_zoom', layerDef.gs_visible_min_zoom ? layerDef.gs_visible_min_zoom : undefined);
          imageLayer.set('visible_min_zoom', layerDef.gs_visible_max_zoom ? layerDef.gs_visible_max_zoom : undefined);

          imageLayer.set('no_features', layerDef.no_features);

          nodeMap.set(layerId, imageLayer);
        } else {
          const group = new OlLayerGroup({
            layers: new OlCollection(),
          });
          group.set('id', `PP_${id}_${layerId}`);
          group.set('title', layerDef.naziv);
          group.set('type', 'app-defined');
          group.set('sortorder', layerDef.sortorder);
          nodeMap.set(layerId, group);
        }
      });

      // Second, wire up parent-child relationships
      (vrste_slojeva || []).forEach((layerDef) => {
        const layerId = layerDef.code;
        const parentCode = layerDef.parent_code;

        if (!layerId || !nodeMap.has(layerId)) return;

        const layer = nodeMap.get(layerId);

        if (parentCode && nodeMap.has(parentCode)) {
          const parent = nodeMap.get(parentCode) as OlLayerGroup;
          const parentCollection = parent.getLayers();
          parentCollection.push(layer as any);
        }
      });

      // Third, collect top-level children (no parent_code)
      const topLevelLayers: (OlLayerGroup | OlLayerImage<OlSourceImageWMS>)[] = [];
      (vrste_slojeva || []).forEach((layerDef) => {
        if (!layerDef.parent_code && nodeMap.has(layerDef.code)) {
          topLevelLayers.push(nodeMap.get(layerDef.code)!);
        }
      });

      // Recursive sort function
      const sortGroupLayers = (group: OlLayerGroup) => {
        const layersArray = group.getLayers().getArray();
        layersArray.sort((a, b) => ((a.get('sortorder') as number) ?? 0) - ((b.get('sortorder') as number) ?? 0));
        group.setLayers(new OlCollection(layersArray));
        layersArray.forEach((l) => {
          if (l instanceof OlLayerGroup) sortGroupLayers(l);
        });
      };

      // Final group representing the IPP
      const rootGroup = new OlLayerGroup({
        layers: new OlCollection(topLevelLayers),
      });

      sortGroupLayers(rootGroup);

      rootGroup.set('id', `PP_${id}`);
      rootGroup.set('title', naziv_short);
      rootGroup.set('type', 'app-defined');

      return rootGroup;
    });

    if (hasDraftPlans) {
      // Draft plans
      const draftSource = new OlSourceImageWMS({
        url: gsUrl,
        params: {
          LAYERS: ['MAP:PP_Draft'],
          FORMAT: 'image/vnd.jpeg-png',
          tenant_id: tenantId,
        },
      });

      const draftPlanLayer = new OlLayerImage({ visible: true, source: draftSource });
      draftPlanLayer.set('id', `prostorni-planovi-drafts`);
      draftPlanLayer.set('title', 'map:layer.prostorni_planovi_drafts');
      draftPlanLayer.set('type', 'app-defined');
      draftPlanLayer.set('format', 'image/vnd.jpeg-png');
      draftPlanLayer.set('query', false);
      draftPlanLayer.set('params', {
        tenant_id: tenantId,
      });
      draftPlanLayer.set('layer', 'MAP:PP_Draft');

      ppGroups.push(draftPlanLayer);
    }

    if (plansWithZahtjevi.length) {
      // Inicijative/zahtjevi
      const planZahtjevLayers: OlLayerImage<OlSourceImageWMS>[] = plansWithZahtjevi.map((plan) => {
        const zahtjevSource = new OlSourceImageWMS({
          url: gsUrl,
          params: {
            LAYERS: ['MAP:PP_Draft'],
            FORMAT: 'image/vnd.jpeg-png',
            tenant_id: tenantId,
            plan_id: plan.id,
          },
        });

        const planZatjevLayer = new OlLayerImage({ visible: true, source: zahtjevSource });

        planZatjevLayer.set('model', ppInicijativeModel);

        planZatjevLayer.set('layer_functionality_id', EnumLayerFunctionality.pp_zahtjevi_button);

        planZatjevLayer.set('id', `prostorni-planovi-zahtjevi-${plan.id}`);
        planZatjevLayer.set('title', plan.naziv_short);
        planZatjevLayer.set('type', 'app-defined');
        planZatjevLayer.set('format', 'image/vnd.jpeg-png');
        planZatjevLayer.set('query', true);
        planZatjevLayer.set('params', {
          tenant_id: tenantId,
          plan_id: plan.id,
        });
        planZatjevLayer.set('layer', 'MAP:PP_zahtjevi');

        return planZatjevLayer;
      });

      const rootIncijativeLayer = new OlLayerGroup({
        properties: {
          id: 'prostorni-planovi-zahtjevi',
          title: 'map:layer.prostorni_planovi_zahtjevi',
          ttoken: 'titles.prostorni_planovi_zahtjevi',
        },
        layers: new OlCollection(planZahtjevLayers),
      });

      ppGroups.push(rootIncijativeLayer);
    }

    if (ppGroups.length > 0) {
      // ROOT Layers
      const rootPPLayer = new OlLayerGroup({
        properties: {
          id: 'prostorni-planovi',
          title: 'map:layer.prostorni_planovi',
          ttoken: 'titles.prostorni_planovi',
        },
        layers: new OlCollection(ppGroups),
      });

      return rootPPLayer;
    }

    return null;
  });

/* const buildTextRecursive = (node: any, level: number = 0): string => {
  let result = '';

  // Format this node's own title/text
  const titlePart = node.numeration ? `${node.numeration} ` : '';
  const valuePart = node.value ? `${node.value}` : '';

  if (titlePart || valuePart) {
    // Indent based on hierarchy level
    const indent = '  '.repeat(level);
    result += `${indent}${titlePart}${valuePart}\n`;
  }

  // Recursively process children
  if (node.children) {
    if (Array.isArray(node.children)) {
      node.children.forEach((child: string) => {
        result += buildTextRecursive(child, level + 1);
      });
    } else if (typeof node.children === 'object') {
      result += buildTextRecursive(node.children, level + 1);
    }
  }

  return result;
}; */

const getPPTextInfo = async (data: IGFICardOverlayData): Promise<IGFICardOverlayData | null> => {
  const { features, position, layerData } = data;
  if (!features?.length) return null;

  const prostorniPlanId = features && features.length ? (features[0].properties.prostorni_plan_id as number) : null;
  if (!prostorniPlanId) return null;

  const currentPlanFeatures = features.filter((f) => f.properties.prostorni_plan_id === prostorniPlanId);

  const allClanakIds = currentPlanFeatures.flatMap((f) => {
    const ids = f.properties?.clanak_ids;
    return typeof ids === 'string' ? ids.replace(/[[\]\s]/g, '').split(',') : [];
  });

  if (!allClanakIds.length) return null;

  const parsedClanakIds = Array.from(new Set(allClanakIds)).filter(Boolean).join(',');

  if (!parsedClanakIds) return null;

  const sortedFeatures = [...currentPlanFeatures].sort((a, b) => {
    const aIsOB = (a.properties?.kod_teme as string)?.startsWith('OB-');
    const bIsOB = (b.properties?.kod_teme as string)?.startsWith('OB-');
    if (aIsOB && !bIsOB) return -1;
    if (!aIsOB && bIsOB) return 1;
    return 0;
  });

  try {
    const resp = await api.get(`prostorni-planovi/${prostorniPlanId}/odjeljci?ids=${parsedClanakIds}`);
    const text = (resp.data as DCRecord).odjeljci as PPTextType[];
    const planName = (resp.data as DCRecord).prostorni_plan_naziv as string;
    const planNameShort = (resp.data as DCRecord).prostorni_plan_naziv_short as string;

    if (!Array.isArray(text) || text.length === 0) return null;

    return {
      type: 'pp',
      position,
      record: {
        text: text as DCRecord[],
        planId: prostorniPlanId,
        planName,
        planNameShort,
      },
      layerData,
      features: sortedFeatures,
    };
  } catch (error) {
    console.error('Error fetching odjeljci:', error);
    return null;
  }
};

const createPPOverlayInput = (
  groupId: string,
  resp: GFIResponseType | null,
  position: CardPosition
): IGFICardOverlayData | null => {
  if (!resp || Object.keys(resp).length === 0) return null;

  const ppKeys = Object.keys(resp).filter((k) => k.startsWith('PP_'));
  if (ppKeys.length === 0) return null;

  const featuresPP = ppKeys.reduce((acc, layerKey) => {
    if (resp[layerKey] && resp[layerKey].length > 0) {
      acc.push(resp[layerKey][0]);
    }

    return acc;
  }, [] as IGFICardOverlayData['features']);

  if (!featuresPP.length) return null;

  return {
    features: featuresPP,
    position,
    type: 'pp',
    layerData: {
      layer: `PP_${  groupId}`,
      model: null,
      title: 'Prostorni planovi',
      layerFunctionalityId: null,
    },
  };
};

const getPPBatchOverlayData = async (
  map: OlMap,
  point: number[],
  ppEntries: Array<[string, OlBaseLayer[]]>,
  position: CardPosition
): Promise<IGFICardOverlayData[]> => {
  const ppOverlayInputsSettled = await Promise.allSettled(
    ppEntries.map(async ([groupId, gsLayers]) => {
      const resp = await gsService.getFeatureInfo(map, point, gsLayers);
      return createPPOverlayInput(groupId, resp, position);
    })
  );

  const ppOverlayInputs = ppOverlayInputsSettled.reduce(
    (acc, result, index) => {
      if (result.status === 'rejected') {
        console.error('Error processing PP GFI request:', result.reason);
        return acc;
      }

      if (result.value) {
        acc.push({
          order: index,
          data: result.value,
        });
      }

      return acc;
    },
    [] as { order: number; data: IGFICardOverlayData }[]
  );

  if (!ppOverlayInputs.length) {
    return [];
  }

  const ppCardsSettled = await Promise.allSettled(
    ppOverlayInputs.map(async ({ order, data }) => ({
      order,
      data: await getPPTextInfo(data),
    }))
  );

  const ppCards = ppCardsSettled.reduce(
    (acc, result) => {
      if (result.status === 'rejected') {
        console.error('Error processing PP text info:', result.reason);
        return acc;
      }

      if (result.value.data) {
        acc.push({
          order: result.value.order,
          data: result.value.data,
        });
      }

      return acc;
    },
    [] as { order: number; data: IGFICardOverlayData }[]
  );

  ppCards.sort((a, b) => a.order - b.order);

  return ppCards.map((entry) => entry.data);
};

// might be the hackiest thing ive ever done

const ppInicijativeModel = {
  fields: [
    { id: 70000224, type: 'numeric', source: 'external_id', ttoken: 'pp:zahtjevi.external_id' },
    { id: 70000225, type: 'text', source: 'plan_name', ttoken: 'pp:zahtjevi.prostorni_plan' },
    { id: 70000226, type: 'text', source: 'podnositelj', ttoken: 'pp:podnositelj' },
    { id: 70000128, type: 'ttoken', source: 'zahtjev_status_token', ttoken: 'pp:zahtjev_status_id' },
    { id: 70000129, type: 'ttoken', source: 'urednost_status_token', ttoken: 'pp:zahtjevi.urednost' },
  ],
  title_token: 'map:layer.pp_zahtjevi',
  title_field: null,
  title_type: 1,
  icon: null, // todo maybe
};

const ppHelper: IPPHelper = {
  getPPLayers,
  getPPTextInfo,
  getPPBatchOverlayData,
};

export default ppHelper;
